import { create } from "zustand";

export type DesktopNavigationAction = "POP" | "PUSH" | "REPLACE";

export interface DesktopNavigationEntry {
  key: string;
  href: string;
}

interface DesktopNavigationState {
  entries: DesktopNavigationEntry[];
  currentIndex: number;
  canGoBack: boolean;
  canGoForward: boolean;
  recordLocation: (entry: DesktopNavigationEntry, action: DesktopNavigationAction) => void;
}

const emptyState = {
  entries: [],
  currentIndex: -1,
  canGoBack: false,
  canGoForward: false,
};

const withCapabilities = (
  entries: DesktopNavigationEntry[],
  currentIndex: number
): Pick<DesktopNavigationState, "entries" | "currentIndex" | "canGoBack" | "canGoForward"> => ({
  entries,
  currentIndex,
  canGoBack: currentIndex > 0,
  canGoForward: currentIndex >= 0 && currentIndex < entries.length - 1,
});

const sameEntry = (a: DesktopNavigationEntry | undefined, b: DesktopNavigationEntry) =>
  a?.key === b.key && a.href === b.href;

export const useDesktopNavigationStore = create<DesktopNavigationState>((set) => ({
  ...emptyState,
  recordLocation: (entry, action) => {
    set((state) => {
      const current = state.entries[state.currentIndex];
      if (sameEntry(current, entry)) {
        return state;
      }

      if (state.currentIndex === -1) {
        return withCapabilities([entry], 0);
      }

      if (action === "REPLACE") {
        const entries = [...state.entries];
        entries[state.currentIndex] = entry;
        return withCapabilities(entries, state.currentIndex);
      }

      if (action === "POP") {
        const existingIndex = state.entries.findIndex((candidate) => candidate.key === entry.key);
        if (existingIndex !== -1) {
          return withCapabilities(state.entries, existingIndex);
        }
      }

      const entries = [...state.entries.slice(0, state.currentIndex + 1), entry];
      return withCapabilities(entries, entries.length - 1);
    });
  },
}));
